import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import PartSidebar from "./PartSidebar";
import PartsCardView from "./PartsCardView";
import PartsListingView from "./PartsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { deactivateData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Parts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Part",
    ENTITY_PLURAL: "Parts",
    DELETE_TITLE_SINGLE: (name) => `Delete Part ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Parts?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete part "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (count) => `You are about to delete ${count} part(s).

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total parts selected: ${count}`,
    DEACTIVATE_TITLE_SINGLE: (name) => `Deactivate Part ${name}?`,
    DEACTIVATE_TITLE_BULK: (count) => `Deactivate ${count} Parts?`,
    DEACTIVATE_WARNING: `Parts are referenced by Job Schedules and production history.

Deactivating hides this part from new selections. Anything already using it keeps working and is not changed.`,
    DEACTIVATE_CONFIRM: "Deactivate",
    DEACTIVATE_CANCEL: "Cancel",
    DEACTIVATED: (name) => `${name} is deactivated`,
    RESTORED: (name) => `${name} is restored`,
    ERROR_NO_USER: "No current user in context",
    NOT_FOUND: "Part not found",
};

const Parts = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routePartId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite   = can("part", "WRITE");
    const canDestroy = can("part", "DESTROY");

    const GET_PARTS_LIST_FOR_PLANT = gql`
        query GetParts($where: PartWhereInput!) {
            parts(where: $where) {
                id
                name
                description
                is_active
                deleted_at
                created_at
                updated_at
                image_asset {
                    url
                }
                part_stages {
                    id
                    operation_id
                    sequence_no
                    standard_duration
                    allowed_devices {
                        device_id
                        position
                    }
                    operation {
                        id
                        name
                        is_productive
                    }
                }
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
            }
        }
    `;

    const UPDATE_MANY_PART_MUTATION = gql`
        mutation UpdateManyPart($data: PartUpdateManyMutationInput!, $where: PartWhereInput) {
            updateManyPart(data: $data, where: $where) {
                count
            }
        }
    `;

    const DELETE_ONE_PART_MUTATION = gql`
        mutation DeleteOnePart($where: PartWhereUniqueInput!) {
            deleteOnePart(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const DELETE_MANY_PARTS_MUTATION = gql`
        mutation DeleteManyParts($ids: [Int!]!) {
            deleteManyPart(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const CREATE_ONE_PART_MUTATION = gql`
        mutation CreateOnePart($data: PartCreateInput!) {
            createOnePart(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_PART_MUTATION = gql`
        mutation UpdateOnePart(
            $data: PartUpdateInput!
            $where: PartWhereUniqueInput!
        ) {
            updateOnePart(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    const CREATE_ONE_PART_STAGE_MUTATION = gql`
        mutation CreateOnePartStage($data: PartStageCreateInput!) {
            createOnePartStage(data: $data) {
                id
                sequence_no
            }
        }
    `;

    const UPDATE_ONE_PART_STAGE_MUTATION = gql`
        mutation UpdateOnePartStage(
            $data: PartStageUpdateInput!
            $where: PartStageWhereUniqueInput!
        ) {
            updateOnePartStage(data: $data, where: $where) {
                id
                sequence_no
            }
        }
    `;

    const DELETE_MANY_PART_STAGE_DEVICE_MUTATION = gql`
        mutation DeleteManyPartStageDevice($partStageId: Int!) {
            deleteManyPartStageDevice(
                where: { part_stage_id: { equals: $partStageId } }
            ) {
                count
            }
        }
    `;

    const DELETE_MANY_PART_STAGE_MUTATION = gql`
        mutation DeleteManyPartStage($ids: [Int!]!) {
            deleteManyPartStage(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const UPLOAD_ASSET_MUTATION = gql`
        mutation UploadAsset($file: Upload!, $semantic: Int!) {
            uploadAsset(file: $file, semantic: $semantic) {
                id
                url
            }
        }
    `;

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const partQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    // network-only on every run, not just the first: flipping "Show Inactive" swaps the
    // query variables, and under the default cache-first Apollo replays whatever that
    // variable set returned last time — a list captured before the row was deactivated
    // or restored. That is why a part toggled under "Show Active" never turned up under
    // "Show Inactive" (CPRN00117-901).
    const { data, loading, refetch } = useQuery(GET_PARTS_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { where: partQueryWhere },
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
    });

    /**
     * Pulls the list again from the server after a write. `updateManyPart` and the
     * delete mutations only return a count, so nothing in the response tells Apollo
     * which Part rows changed — the list has to be re-read to pick up `is_active`.
     *
     * A failed refetch must not take the write down with it: the row is already saved,
     * and throwing here would surface as a bogus "delete failed".
     */
    const hardRefresh = async () => {
        try {
            await refetch();
        } catch (err) {
            console.error("Failed to refresh parts:", err);
        }
    };

    const [deleteOnePart] = useMutation(DELETE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPart] = useMutation(DELETE_MANY_PARTS_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOnePart] = useMutation(CREATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateOnePart] = useMutation(UPDATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [uploadAsset] = useMutation(UPLOAD_ASSET_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOnePartStage] = useMutation(CREATE_ONE_PART_STAGE_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPartStageDevice] = useMutation(
        DELETE_MANY_PART_STAGE_DEVICE_MUTATION,
        { client: starekContext.engineClient },
    );
    const [updateOnePartStage] = useMutation(UPDATE_ONE_PART_STAGE_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPartStage] = useMutation(DELETE_MANY_PART_STAGE_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateManyPart] = useMutation(UPDATE_MANY_PART_MUTATION, { client: starekContext.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const [isTogglingActive, setIsTogglingActive] = useState(false);

    const [showAddPartDrawer, setShowAddPartDrawer] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [viewMode, setViewMode] = useState("card");
    const [routeEditPart, setRouteEditPart] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    const inactiveRetryRef = useRef(null);
    const [toggleCleared, setToggleCleared] = useState(false);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);

    const items = useMemo(() => data?.parts ?? [], [data?.parts]);

    // ?tab=route deep-links straight to a part's Route, which is where a freshly
    // created part has to go next. Anything else opens on Basic Details as before.
    const initialSidebarPage =
        searchParams.get("tab") === "route" ? "operations" : "basic_details";

    useEffect(() => {
        if (!routeId) {
            setRouteEditPart(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/parts", searchParams);

        if (routePartId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditPart(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const partToEdit = items.find(
            (part) => Number(part?.id) === routePartId,
        );

        if (!partToEdit) {
            // The part may simply be inactive and filtered out of the default list —
            // deactivating one from its own panel lands here. Widen the query once
            // before concluding it does not exist.
            if (!showInactive && inactiveRetryRef.current !== routePartId) {
                inactiveRetryRef.current = routePartId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditPart(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditPart(partToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routePartId
                ? current
                : [partToEdit],
        );
        setShowAddPartDrawer(true);
    }, [
        routeId,
        routePartId,
        loading,
        items,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        starekContext,
    ]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    /**
     * Persists a part's operation sequence.
     *
     * Rows already in the database keep their id and get their sequence_no and override
     * refreshed; new rows are created; rows the user removed are deleted. sequence_no
     * arrives already renumbered contiguously from the sidebar.
     */
    const persistPartStages = async (
        partId,
        partOperations = [],
        deletedPartStageIds = [],
    ) => {
        if (deletedPartStageIds.length) {
            await deleteManyPartStage({
                variables: { ids: deletedPartStageIds.map(Number) },
            });
        }

        // Position carries the preference: 0 is the machine the step normally runs on.
        const buildAllowedDevices = (deviceIds = []) => ({
            create: deviceIds.map((deviceId, index) => ({
                position: index,
                device: { connect: { id: deviceId } },
            })),
        });

        for (const row of partOperations) {
            if (row.id) {
                // Allowed devices are replaced wholesale — they carry no state of their own.
                await deleteManyPartStageDevice({
                    variables: { partStageId: Number(row.id) },
                });
                await updateOnePartStage({
                    variables: {
                        where: { id: Number(row.id) },
                        data: {
                            sequence_no: { set: row.sequence_no },
                            standard_duration: {
                                set: row.standard_duration ?? null,
                            },
                            operation: {
                                connect: { id: Number(row.operation_id) },
                            },
                            allowed_devices: buildAllowedDevices(
                                row.device_ids,
                            ),
                        },
                    },
                });
            } else {
                await createOnePartStage({
                    variables: {
                        data: {
                            sequence_no: row.sequence_no,
                            standard_duration: row.standard_duration ?? null,
                            part: { connect: { id: Number(partId) } },
                            operation: {
                                connect: { id: Number(row.operation_id) },
                            },
                            allowed_devices: buildAllowedDevices(
                                row.device_ids,
                            ),
                        },
                    },
                });
            }
        }
    };

    const handleSavePart = async (partPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id;

            if (!currentUserId) {
                throw new Error(UI_STRINGS.ERROR_NO_USER);
            }

            let assetId = null;
            if (partPayload.image_file) {
                const { data: assetData } = await uploadAsset({
                    variables: { file: partPayload.image_file, semantic: 0 },
                });
                assetId = assetData?.uploadAsset?.id;
            }

            const base = {
                name: partPayload.name,
                description: partPayload.description || "",
                plant: { connect: { id: Number(partPayload.plant_id) } },
                ...(assetId && {
                    image_asset: { connect: { id: assetId } },
                }),
            };

            // Handed back to the sidebar so its next step can deep-link this part.
            let savedPartId = partPayload.id ?? null;

            if (!partPayload.id) {
                // Create
                const data = {
                    ...base,
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                const { data: created } = await createOnePart({
                    variables: { data },
                });
                const newPartId = created?.createOnePart?.id;
                savedPartId = newPartId ?? null;
                if (newPartId) {
                    await persistPartStages(
                        newPartId,
                        partPayload.partOperations,
                        [],
                    );
                }
            } else {
                // Update - only send fields that have values
                const data = {
                    updated_by: { connect: { id: Number(currentUserId) } },
                };

                if (partPayload.name) {
                    data.name = { set: partPayload.name };
                }

                if (partPayload.description !== undefined) {
                    data.description = { set: partPayload.description || "" };
                }

                if (assetId) {
                    data.image_asset = { connect: { id: assetId } };
                }

                await updateOnePart({
                    variables: { data, where: { id: partPayload.id } },
                });

                await persistPartStages(
                    partPayload.id,
                    partPayload.partOperations,
                    partPayload.deletedPartStageIds,
                );
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await hardRefresh();
            starekContext.showSuccess(
                partPayload.id
                    ? `Part "${partPayload.name}" updated successfully`
                    : `Part "${partPayload.name}" created successfully`,
            );
            return { id: savedPartId };
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/parts/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setRouteEditPart(null);
        setSelectedRows([]);
        setShowAddPartDrawer(true);
    };

    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        setRouteEditPart(null);
        setShowAddPartDrawer(true);
    };

    /**
     * Resolves only once the confirm dialog is answered — true when the parts were
     * deleted, false when the user backed out.
     *
     * The sidebar's Delete button awaits this. Before, it got back `undefined`, so
     * `SidebarContextProvider` treated the delete as finished the moment the dialog
     * opened and tore the panel down on top of it, leaving the confirm unusable.
     */
    const handleDelete = (parts) =>
        new Promise((resolve) => {
            if (!parts || parts.length === 0 || isDeleting) {
                resolve(false);
                return;
            }

            const isSingle = parts.length === 1;
            const title = isSingle
                ? UI_STRINGS.DELETE_TITLE_SINGLE(parts[0].name)
                : UI_STRINGS.DELETE_TITLE_BULK(parts.length);

            const message = isSingle
                ? UI_STRINGS.DELETE_MESSAGE_SINGLE(parts[0].name)
                : UI_STRINGS.DELETE_MESSAGE_BULK(parts.length);

            openConfirm({
                title: title,
                content: (
                    <>
                        <div
                            className="text-dark"
                            style={{ whiteSpace: "pre-wrap" }}
                        >
                            {message}
                        </div>
                        <p className="mt-4 text-dark fw-bold">
                            {UI_STRINGS.TOTAL_SELECTED(parts.length)}
                        </p>
                    </>
                ),
                confirmText: UI_STRINGS.DELETE_CONFIRM,
                cancelText: UI_STRINGS.DELETE_CANCEL,
                type: "danger",
                onConfirm: async () => {
                    setIsDeleting(true);
                    setDeleteDone(0);

                    const ids = parts.map((p) => p.id);

                    try {
                        // Try batch delete first
                        await deleteManyPart({ variables: { ids } });
                        setDeleteDone(ids.length);
                        starekContext.showSuccess(
                            `Successfully deleted ${ids.length} part(s)`,
                        );
                    } catch {
                        // Fallback to individual deletes
                        for (const id of ids) {
                            try {
                                await deleteOnePart({
                                    variables: { where: { id } },
                                });
                                setDeleteDone((d) => d + 1);
                            } catch (err) {
                                starekContext.showError?.(err);
                                break;
                            }
                        }
                    } finally {
                        setIsDeleting(false);
                        setSelectedRows([]);
                        setToggleCleared((p) => !p);
                        await hardRefresh();
                        closeConfirm();
                        resolve(true);
                    }
                },
                onClose: () => {
                    closeConfirm();
                    resolve(false);
                },
            });
        });

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddPartDrawer(false);
        setRouteEditPart(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            // ?tab belongs to the part being closed, not to the list behind it.
            const listParams = new URLSearchParams(searchParams);
            listParams.delete("tab");
            navigate(buildListRoute("/parts", listParams), {
                replace: true,
            });
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
        await hardRefresh();
    };

    /**
     * Deactivating is warned about but never blocked — a part already on a job schedule
     * keeps running. Restoring needs no warning, so it skips the dialog.
     */
    const confirmDeactivate = (parts, proceed) => {
        const isSingle = parts.length === 1;
        openConfirm({
            title: isSingle
                ? UI_STRINGS.DEACTIVATE_TITLE_SINGLE(parts[0].name)
                : UI_STRINGS.DEACTIVATE_TITLE_BULK(parts.length),
            content: (
                <div className="text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {UI_STRINGS.DEACTIVATE_WARNING}
                </div>
            ),
            confirmText: UI_STRINGS.DEACTIVATE_CONFIRM,
            cancelText: UI_STRINGS.DEACTIVATE_CANCEL,
            type: "warning",
            onConfirm: async () => {
                await proceed();
                closeConfirm();
            },
            onClose: closeConfirm,
        });
    };

    const applyToggleActive = async (parts, makeActive) => {
        const ids = parts.map((p) => p.id);
        setIsTogglingActive(true);
        try {
            await updateManyPart({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(
                makeActive
                    ? `Restored ${ids.length} part(s)`
                    : `Deactivated ${ids.length} part(s)`,
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setIsTogglingActive(false);
        }
    };

    const handleToggleActive = async (parts, makeActive) => {
        if (!parts || parts.length === 0) return;
        if (makeActive) {
            await applyToggleActive(parts, true);
            return;
        }
        confirmDeactivate(parts, () => applyToggleActive(parts, false));
    };

    const handleToggleActiveSingle = async (part) => {
        const deactivating = part.is_active;

        const apply = async () => {
            setTogglingActiveId(part.id);
            setIsTogglingActive(true);
            try {
                await updateManyPart({
                    variables: {
                        data: deactivating ? deactivateData() : restoreData(),
                        where: { id: { in: [part.id] } },
                    },
                });
                starekContext.showSuccess(
                    deactivating
                        ? UI_STRINGS.DEACTIVATED(part.name)
                        : UI_STRINGS.RESTORED(part.name),
                );
                await hardRefresh();
            } catch (err) {
                starekContext.showError(err);
            } finally {
                setTogglingActiveId(null);
                setIsTogglingActive(false);
            }
        };

        if (!deactivating) {
            await apply();
            return;
        }
        confirmDeactivate([part], apply);
    };

    const partExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    // Only the first load gets the full-page spinner. Later loads — the "Show Inactive"
    // toggle swapping variables, a refetch after a write — keep the list on screen, so
    // an open sidebar or confirm dialog is not torn down mid-action.
    if (loading && !data) return <EntityLoadingState icon="fa-cube" />;

    const activePart = routeEditPart || selectedRows[0] || null;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={partExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    patchSearchParams({ view: mode });
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEditSelected}
            >
                <EntityOperationBar.View
                    mode="card"
                    icon="fa-th"
                    tooltip={UI_STRINGS.VIEW_MODE_CARD}
                >
                    <PartsCardView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onAdd={handleAdd}
                        onRowClicked={handleRowClicked}
                        onToggleActive={canWrite ? handleToggleActiveSingle : undefined}
                        togglingActiveId={togglingActiveId}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <PartsListingView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-cube" entityName="Part" messages={EMPTY_MESSAGES.parts} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddPartDrawer && (
                <PartSidebar
                    show={true}
                    item={activePart}
                    plantId={starekContext.lastUsedPlant.id}
                    onDiscard={handleDrawerClose}
                    onSave={handleSavePart}
                    onDelete={(part) => handleDelete([part])}
                    onToggleActive={canWrite ? handleToggleActiveSingle : undefined}
                    initialPage={initialSidebarPage}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting || isTogglingActive}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Parts };
